import { useLayoutEffect, useState } from "react";

import { memoize } from "lodash";

// Note: technically, this will keep every unique media query we ever use in memory – we'd have to use
// a huge number for that to become a problem, but it's worth noting.
const matchQuery = memoize(window.matchMedia);

/**
 * A hook for checking a media query. Used like so:
 *
 * ```tsx
 * const isMobile = useMedia("(max-width: 767px)");
 * return isMobile ? <MobileVersion /> : <DesktopVersion />;
 * ```
 *
 * @param	query - The media query to test - if it's a simple media feature query, e.g. `(max-width: 1000px)`,
 * 	it must be enclosed in parentheses.
 * @returns True if the query matches, false otherwise. If the query is invalid, returns false.
 */
export default function useMedia(query: string): boolean {
	const watcher = matchQuery(query);
	const [matches, setMatches] = useState(watcher.matches);

	useLayoutEffect(() => {
		function updateMatches(ev: MediaQueryListEvent) {
			setMatches(ev.matches);
		}
		// the spec prefers `MediaQueryList.addEventListener`, but Safari only added support for it in v14
		watcher.addListener(updateMatches);
		return () => watcher.removeListener(updateMatches);
	}, [watcher]);

	return matches;
}
